import { useState, FunctionComponent, memo, useMemo, useCallback, useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import {
  BriefcaseIcon,
  ArrowLeftOnRectangleIcon,
  UserIcon,
  HomeIcon,
  BuildingOfficeIcon,
  DocumentArrowUpIcon,
  CreditCardIcon,
  UsersIcon,
  Bars3CenterLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { GiftIcon } from '@heroicons/react/24/solid';
import { useIntercom } from 'react-use-intercom';

import { ButtonV2, IconButton, IconComponentType, NavMenuItem, V2Link } from '@restworld/ui-ds';
// import { useEmployerUserInformationQuery } from '../components/user-settings/use-employer-user-information';
import { LayoutWrapperOld, ILayoutWrapperProps } from './layout-wrapper-old';
import { useAuth, useEmployerUserContactData, useIsEmployerAnAdmin } from '@restworld/feature-authentication';
import {
  getAssetSrc,
  HeadphoneAltIcon,
  useAnalytics,
  useIsFreemiumUser,
  PremiumBadgeIcon,
} from '@restworld/utils-common';
import { Logo } from '@restworld/ui-ds-base';

import { useV2Router } from '../components/dashboard/hooks/use-v2-router';

export const LayoutWrapper: FunctionComponent<ILayoutWrapperProps> = ({
  children,
  title,
  isNavbarOffScreen,
  hideNavigation,
  hasFooter,
  hasNavbar,
  navbarVariant,
  showLegacyLayout,
}) => {
  const { isFallback } = useRouter();

  if (showLegacyLayout) {
    return (
      <LayoutWrapperOld title={title} hasFooter={hasFooter} hasNavbar={hasNavbar} navbarVariant={navbarVariant}>
        {children}
      </LayoutWrapperOld>
    );
  }

  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <link rel="shortcut icon" href={getAssetSrc('/utils-assets/favicons/favicon.ico')} />
        <link
          rel="icon"
          type="image/png"
          href={getAssetSrc('/utils-assets/favicons/favicon-96x96.png')}
          sizes="96x96"
        />
        <link rel="icon" type="image/svg+xml" href={getAssetSrc('/utils-assets/favicons/favicon.svg')} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={getAssetSrc('/utils-assets/favicons/apple-touch-icon.png')}
        />
        <link rel="stylesheet" href="https://unpkg.com/flowbite@1.4.5/dist/flowbite.min.css" />
        <title>{isFallback ? 'loading...' : title}</title>
      </Head>

      <NavigationContainer>
        {!hideNavigation ? (
          <>
            <DesktopSideNavigationBar />
            <MobileNavigation isNavbarOffScreen={isNavbarOffScreen} />
            <PageContentWrapper $hasNavigation>{children}</PageContentWrapper>
          </>
        ) : (
          <PageContentWrapper>{children}</PageContentWrapper>
        )}
      </NavigationContainer>
    </>
  );
};
export default memo(LayoutWrapper);

function DesktopSideNavigationBar() {
  const [isHovered, setIsHovered] = useState(false);
  const { navItems, isPpuUser } = useNavItems();
  const { primaryNavItems, secondaryNavItems, actionNavItems } = useNavItemElements({ isOpen: isHovered, navItems });
  const isFreemiumUser = useIsFreemiumUser();

  return (
    <SideNavWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      isHovered={isHovered}
    >
      <NavContentWrapper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        isHovered={isHovered}
      >
        <div>
          <RwLogo />
          <RoutesWrapper>
            <PrimaryRoutesWrapper>{primaryNavItems}</PrimaryRoutesWrapper>
            <SecondaryRoutesWrapper>
              {secondaryNavItems}
              {isPpuUser && <ReferralNavCTA isOpen={isHovered} surface="desktop_sidebar" />}
            </SecondaryRoutesWrapper>
          </RoutesWrapper>
          {isFreemiumUser && isHovered && <UpgradeToPremium />}
        </div>
        <ActionsWrapper>{actionNavItems}</ActionsWrapper>
      </NavContentWrapper>
    </SideNavWrapper>
  );
}

function MobileNavigation({ isNavbarOffScreen }: { isNavbarOffScreen?: boolean }) {
  const { t } = useTranslation('common');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { navItems, isPpuUser } = useNavItems();
  const { primaryNavItems, secondaryNavItems, actionNavItems } = useNavItemElements({ isOpen: true, navItems });
  const isFreemiumUser = useIsFreemiumUser();

  useEffect(() => {
    document.body.style.overflow = isNavOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isNavOpen]);

  return (
    <MobileNavContainer $isOpen={isNavOpen} $isNavbarOffScreen={isNavOpen ? false : isNavbarOffScreen}>
      <MobileNavHeader>
        <RwLogo />
        <IconButtonWrapper $isOpen={isNavOpen}>
          <IconButton
            ariaLabel={isNavOpen ? t('close_navigation_menu_key') : t('open_navigation_menu_key')}
            Icon={isNavOpen ? XMarkIcon : Bars3CenterLeftIcon}
            onClick={() => setIsNavOpen((prev) => !prev)}
          />
        </IconButtonWrapper>
      </MobileNavHeader>
      <MobileNavScrollContent $isOpen={isNavOpen}>
        <RoutesWrapper>
          <PrimaryRoutesWrapper>{primaryNavItems}</PrimaryRoutesWrapper>
          <SecondaryRoutesWrapper>
            {secondaryNavItems}
            {isPpuUser && <ReferralNavCTA isOpen={isNavOpen} surface="mobile_menu" appearanceDelayMs={0} />}
          </SecondaryRoutesWrapper>
          {isFreemiumUser && <UpgradeToPremium />}
        </RoutesWrapper>
        <ActionsWrapper>{actionNavItems}</ActionsWrapper>
      </MobileNavScrollContent>
    </MobileNavContainer>
  );
}

function RwLogo() {
  return (
    <V2Link href="/employer/homepage">
      <Logo variant="mark" title="restworld" className="h-10 w-10 text-purple-50" rColor="#482485" />
    </V2Link>
  );
}

function UpgradeToPremium() {
  const { t } = useTranslation('common');
  const router = useV2Router();
  const [showComponent, setShowComponent] = useState(false);

  useEffect(() => {
    // Delay the rendering of the component to account for sidenav animation time
    const timeoutId = setTimeout(() => {
      setShowComponent(true);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  if (!showComponent) return;

  return (
    <div tw="w-full rounded-lg bg-black py-3 px-4 flex flex-col gap-[10px] items-center justify-center text-sm! text-purple-50 font-medium mt-4 text-center bg-[url('/utils-assets/rw-logo-white.png')] bg-no-repeat bg-cover">
      <PremiumBadgeIcon stroke="#1C1327" fill="#F0EBF8" />
      <div>{t('do_you_need_more_support_in_your_staff_search')}</div>
      <ButtonV2 size="sm" onClick={() => router.push('/employer/purchase')}>
        {t('find_out_more')}
      </ButtonV2>
    </div>
  );
}

const BASE_ROUTE = '/employer';
enum RouteKey {
  DASHBOARD = 'homepage',
  JOB_POSITIONS = 'job-positions',
  VENUES = 'venues',
  SPONTANEOUS_APPLICATIONS = 'spontaneous-applications',
  PAYMENTS_AND_INVOICES = 'payments-and-invoices',
  REFERRAL = 'referral',
  COLLABORATORS = 'collaborators',
  ACCOUNT = 'account',
}

function useNavItems() {
  const { t } = useTranslation('common');
  const employerUserContactData = useEmployerUserContactData();
  const router = useRouter();
  const { logout, user } = useAuth();
  const { trackEvent } = useAnalytics();
  // const setShowContactRestworldManagerModal = useSetAtom(showContactRestWorldManagerModalAtom);
  const avatarImageURL = useMemo(() => employerUserContactData?.picture_url, [employerUserContactData]);
  const isAdmin = useIsEmployerAnAdmin();
  const isFreemiumUser = useIsFreemiumUser();
  const { showMessages } = useIntercom();
  const currentRoute = router.pathname;

  const getSupport = useCallback(() => {
    showMessages();
    trackEvent('employer_click_support', {
      source_page: getLastPathSegment(currentRoute),
      is_from_nav_bar: true,
      email: user?.email,
      phone: user?.phone_number,
    });
    // setShowContactRestworldManagerModal(true);
  }, [currentRoute, showMessages, trackEvent, user?.email, user?.phone_number]);

  const navItems: NavItem[] = useMemo(
    () => [
      {
        route: `${BASE_ROUTE}/homepage`,
        title: t('homepage_key'),
        Icon: HomeIcon,
        type: 'primary',
        key: RouteKey.DASHBOARD,
      },
      {
        route: `${BASE_ROUTE}/job-positions`,
        title: t('job_positions_key'),
        Icon: BriefcaseIcon,
        type: 'primary',
        key: RouteKey.JOB_POSITIONS,
        hasPremiumBadgeIcon: isFreemiumUser,
      },
      {
        route: `${BASE_ROUTE}/venues`,
        title: t('venues_key'),
        Icon: BuildingOfficeIcon,
        type: 'primary',
        key: RouteKey.VENUES,
      },
      {
        route: `${BASE_ROUTE}/spontaneous-applications`,
        title: t('spontaneous_applications_key'),
        Icon: DocumentArrowUpIcon,
        type: 'primary',
        key: RouteKey.SPONTANEOUS_APPLICATIONS,
      },
      {
        route: `${BASE_ROUTE}/payments-and-invoices`,
        title: t('payments_and_invoices_key'),
        Icon: CreditCardIcon,
        type: 'secondary',
        key: RouteKey.PAYMENTS_AND_INVOICES,
      },
      {
        route: `${BASE_ROUTE}/collaborators`,
        title: t('collaborators_key'),
        Icon: UsersIcon,
        type: 'secondary',
        key: RouteKey.COLLABORATORS,
      },
      {
        route: `${BASE_ROUTE}/account`,
        title: t('account_key'),
        Icon: UserIcon,
        imageUrl: avatarImageURL,
        type: 'action',
        key: RouteKey.ACCOUNT,
      },
      {
        title: t('support_key'),
        Icon: HeadphoneAltIcon,
        type: 'action',
        onClick: getSupport,
      },
      {
        title: t('logout_key'),
        Icon: ArrowLeftOnRectangleIcon,
        type: 'action',
        onClick: logout,
      },
    ],
    [avatarImageURL, getSupport, isFreemiumUser, logout, t]
  );

  const isPpuUser = !!user?.using_ppu_business_model;

  const currentNavItems = useMemo(() => {
    let items = navItems;
    if (!(isAdmin && !isFreemiumUser)) {
      items = items.filter((v) => v.key !== RouteKey.PAYMENTS_AND_INVOICES);
    }
    return items;
  }, [isAdmin, isFreemiumUser, navItems]);

  return { navItems: currentNavItems, isPpuUser };
}

function useNavItemElements({ isOpen, navItems }: { navItems: NavItem[]; isOpen: boolean }) {
  const router = useRouter();
  const currentRoute = router.pathname;
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();

  const handleEventTracking = useCallback(
    (route: string) => {
      if (route.includes(RouteKey.DASHBOARD)) {
        trackEvent('employer_click_homepage', { email: user?.email, phone: user?.phone_number });
      } else if (route.includes(RouteKey.JOB_POSITIONS)) {
        trackEvent('employer_click_job_positions', { email: user?.email, phone: user?.phone_number });
      } else if (route.includes(RouteKey.VENUES)) {
        trackEvent('employer_click_venues', { email: user?.email, phone: user?.phone_number });
      } else if (route.includes(RouteKey.SPONTANEOUS_APPLICATIONS)) {
        trackEvent('employer_click_spontaneous_applications', { email: user?.email, phone: user?.phone_number });
      }
    },
    [trackEvent, user?.email, user?.phone_number]
  );

  const primaryNavItems = useMemo(
    () =>
      navItems
        .filter((item) => item.type === 'primary')
        .map(({ title, Icon, route, key, hasPremiumBadgeIcon }) => (
          <NavMenuItem
            key={title}
            Icon={Icon}
            isOpen={isOpen}
            isSelected={currentRoute.includes(key)}
            href={route}
            onClick={() => handleEventTracking(route)}
          >
            {title}
            {!!hasPremiumBadgeIcon && <PremiumBadgeIcon />}
          </NavMenuItem>
        )),
    [currentRoute, handleEventTracking, isOpen, navItems]
  );
  const secondaryNavItems = useMemo(
    () =>
      navItems
        .filter((item) => item.type === 'secondary')
        .map(({ title, Icon, route, key, hasPremiumBadgeIcon }) => (
          <NavMenuItem
            key={title}
            Icon={Icon}
            isOpen={isOpen}
            isSelected={currentRoute.includes(key)}
            href={route}
            onClick={() => {
              if (route.includes(RouteKey.PAYMENTS_AND_INVOICES)) {
                trackEvent('employer_view_payment', { email: user?.email, phone: user?.phone_number });
              }
            }}
          >
            {title}
            {!!hasPremiumBadgeIcon && <PremiumBadgeIcon />}
          </NavMenuItem>
        )),
    [currentRoute, isOpen, navItems, trackEvent, user?.email, user?.phone_number]
  );
  const actionNavItems = useMemo(
    () =>
      navItems
        .filter((item) => item.type === 'action')
        .map(({ title, Icon, route, imageUrl, onClick, key, hasPremiumBadgeIcon }) => (
          <NavMenuItem
            key={title}
            Icon={Icon}
            isOpen={isOpen}
            isSelected={currentRoute.includes(key)}
            href={route}
            imageUrl={imageUrl}
            onClick={onClick}
            isLink={!onClick}
          >
            {title}
            {!!hasPremiumBadgeIcon && <PremiumBadgeIcon />}
          </NavMenuItem>
        )),
    [currentRoute, isOpen, navItems]
  );

  return { primaryNavItems, secondaryNavItems, actionNavItems };
}

interface StyledWrapperProps {
  isHovered?: boolean;
}
type NavItemType = 'primary' | 'secondary' | 'action';
interface NavItem {
  title: string;
  type: NavItemType;
  route?: string;
  key?: RouteKey;
  Icon?: IconComponentType;
  imageUrl?: string;
  onClick?: () => void;
  hasPremiumBadgeIcon?: boolean;
}

const REFERRAL_CTA_DESKTOP_APPEARANCE_DELAY_MS = 175;

function ReferralNavCTA({
  isOpen,
  surface,
  appearanceDelayMs = REFERRAL_CTA_DESKTOP_APPEARANCE_DELAY_MS,
}: {
  isOpen: boolean;
  surface: 'desktop_sidebar' | 'mobile_menu';
  appearanceDelayMs?: number;
}) {
  const { t } = useTranslation('common');
  const router = useV2Router();
  const { trackEvent } = useAnalytics();
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowButton(false);
      return;
    }
    if (appearanceDelayMs <= 0) {
      setShowButton(true);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setShowButton(true);
    }, appearanceDelayMs);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, appearanceDelayMs]);

  if (!showButton) {
    return null;
  }

  return (
    <ReferralCTAButton
      onClick={() => {
        trackEvent('referral_nav_cta_clicked', { surface });
        router.push('/employer/referral');
      }}
    >
      <GiftIcon width={16} height={16} color="#DCD7FE" />
      <ReferralCTALabel>{t('nav_get_free_credits')}</ReferralCTALabel>
    </ReferralCTAButton>
  );
}

function getLastPathSegment(path: string) {
  return path.split('/').filter(Boolean).pop() || '';
}

const NavigationContainer = styled.div(() => [tw`w-full bg-neutral-10 min-h-screen xl:flex`]);
const SideNavWrapper = styled.div<StyledWrapperProps>`
  ${tw`w-[88px] min-w-[88px] max-w-[88px] hidden xl:flex`}
`;
const PageContentWrapper = styled.div<{ $hasNavigation?: boolean }>`
  ${tw`w-full`}
  ${({ $hasNavigation }) => $hasNavigation && tw`pt-[96px] xl:pt-20`}
`;
const NavContentWrapper = styled.div<StyledWrapperProps>`
  ${tw`fixed z-50 top-2 bottom-2 left-2 w-20 transition-all duration-200 p-5 border border-neutral-40 rounded-2xl bg-neutral-10 flex flex-col justify-between shadow-[0px_2px_8px_0px_rgba(0,0,26,0.1)]`}
  ${({ isHovered }) => (isHovered ? tw`w-72 min-w-[288px] max-w-[288px]` : tw`w-20 min-w-[80px] max-w-[80px]`)}
`;
const RoutesWrapper = styled.div`
  ${tw`mt-7`}
`;
const ActionsWrapper = styled.div`
  ${tw`flex flex-col gap-3 border-neutral-40 pt-6 border-t pb-[14px] xl:(border-t-0 pt-0 pb-0)`}
`;
const PrimaryRoutesWrapper = styled.div`
  ${tw`pb-5 flex flex-col gap-3`}
`;
const SecondaryRoutesWrapper = styled.div`
  ${tw`pt-5 pb-5 border-t border-neutral-40 flex flex-col gap-3`}
`;
const MobileNavContainer = styled.div<{ $isOpen?: boolean; $isNavbarOffScreen?: boolean }>`
  ${tw`fixed left-2 right-2 bg-neutral-10 rounded-2xl border border-neutral-40 shadow-[0px_2px_8px_0px_rgba(0,0,26,0.1)] z-[60] xl:hidden transition-all duration-200 flex flex-col overflow-hidden`}
  ${({ $isNavbarOffScreen }) => ($isNavbarOffScreen ? tw`-top-96` : tw`top-2`)}
  ${({ $isOpen }) => ($isOpen ? tw`h-[calc(100vh_-_16px)]` : tw`h-[64px]`)}
`;
const MobileNavHeader = styled.div`
  ${tw`h-[64px] min-h-[64px] px-3 py-3 flex items-center justify-between`}
`;
const IconButtonWrapper = styled.div<{ $isOpen?: boolean }>`
  ${tw`w-fit transition-transform duration-200`}
  ${({ $isOpen }) => ($isOpen ? tw`transform rotate-180` : tw`transform rotate-0`)}
`;
const MobileNavScrollContent = styled.div<{ $isOpen?: boolean }>`
  ${tw`flex-1 flex flex-col justify-between overflow-y-auto px-3 pb-3`}
  ${({ $isOpen }) => ($isOpen ? tw`opacity-100` : tw`opacity-0 pointer-events-none`)}
`;

const ReferralCTAButton = styled.button`
  ${tw`flex items-center justify-center w-full cursor-pointer border-none mt-3`}
  gap: 4px;
  padding: 12px 16px;
  border-radius: 8px;
  background: radial-gradient(circle at 2% 10%, rgba(72, 36, 133, 1) 0%, rgba(28, 28, 51, 1) 100%);
  box-shadow: 0px 2px 8px 0px rgba(102, 51, 187, 0.5);
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`;

const ReferralCTALabel = styled.span`
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.21;
  color: #dcd7fe;
`;
